import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { Link as RouterLink } from "react-router-dom";
import { client } from "@/lib/api";
import BreakChainFlow from "@/components/BreakChainFlow";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { Unlink, LifeBuoy } from "lucide-react";

const Ctx = createContext(null);
export const useBreakChain = () => useContext(Ctx) || { open: () => {} };

export function BreakChainProvider({ children }) {
  const [phase, setPhase] = useState(null); // 'intervene' | 'help'
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [helpMsg, setHelpMsg] = useState("");

  const close = () => { setPhase(null); setSel(null); setBusy(false); };

  const open = useCallback(async () => {
    try {
      const res = await client.get("/plans");
      const plans = res.data || [];
      if (!plans.length) {
        setHelpMsg("Let's set up your quit plan first — then this is here the moment an urge hits.");
        setPhase("help");
      } else {
        setSel(plans[0]); setPhase("intervene");
      }
    } catch (e) {
      toast.error("Couldn't open right now. Please try again.");
    }
  }, []);

  // PWA home-screen shortcut: /app?bc=1 opens Break the Chain immediately.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("bc") === "1") {
      open();
      params.delete("bc");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [open]);

  const doResist = async () => {
    setBusy(true);
    try {
      await client.post("/taper/resist", { plan_id: sel.id });
      toast.success("Chain break recorded. You interrupted the pattern — a new response is taking root.");
      close();
    } catch { toast.error("Couldn't save that. Try again."); setBusy(false); }
  };
  const doLog = async () => {
    setBusy(true);
    try {
      await client.post("/taper/log", { plan_id: sel.id });
      toast("Logged — no shame. You showed up honestly. Keep going, one moment at a time.");
      close();
    } catch { toast.error("Couldn't save that. Try again."); setBusy(false); }
  };

  return (
    <Ctx.Provider value={{ open }}>
      {children}

      <ResponsiveModal
        open={phase === "help"}
        onOpenChange={(o) => !o && close()}
        testid="breakchain-help"
        icon={<div className="rounded-xl bg-sage p-2 shrink-0"><LifeBuoy className="w-4 h-4 text-brand-teal-dark" /></div>}
        title="Right here with you"
        description={helpMsg}
      >
        <div className="flex flex-col sm:flex-row gap-2.5 pb-2">
          <RouterLink to="/app/coach" onClick={close} className="flex-1 rounded-full bg-terracotta text-white text-center py-3 text-sm font-medium hover:bg-terracotta-soft transition-colors">Talk with Companion</RouterLink>
          <RouterLink to="/app/journey" onClick={close} className="flex-1 rounded-full border border-border text-olive text-center py-3 text-sm hover:bg-sage transition-colors">My Journey</RouterLink>
        </div>
      </ResponsiveModal>

      {sel && (
        <BreakChainFlow
          open={phase === "intervene"}
          planId={sel.id}
          substance={sel.substance}
          onResisted={doResist}
          onLogAnyway={doLog}
          onClose={close}
        />
      )}
    </Ctx.Provider>
  );
}

export function BreakChainButton({ variant = "sidebar", className = "" }) {
  const { open } = useBreakChain();
  if (variant === "banner") {
    return (
      <button data-testid="break-chain-banner" onClick={open}
        className={`w-full flex items-center gap-4 rounded-3xl bg-brand-navy text-white p-6 text-left transition-transform duration-200 hover:scale-[1.01] shadow-soft ${className}`}>
        <div className="rounded-2xl bg-white/10 p-3 shrink-0"><Unlink className="w-6 h-6 text-brand-teal" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-h3 leading-none">Break the Chain</p>
          <p className="text-white/70 text-sm mt-1.5">Feeling an urge right now? Pause here first — you don't have to white-knuckle it.</p>
        </div>
      </button>
    );
  }
  if (variant === "mobile") {
    return (
      <button data-testid="break-chain-mobile" onClick={open} aria-label="Break the Chain"
        className={`flex items-center gap-1.5 rounded-full bg-terracotta text-white px-3.5 py-2 text-sm font-semibold transition-colors duration-200 hover:bg-terracotta-soft shadow-glow-teal ${className}`}>
        <Unlink className="w-4 h-4" /> Break the Chain
      </button>
    );
  }
  return (
    <button data-testid="break-chain-btn" onClick={open}
      className={`flex items-center justify-center gap-2 rounded-2xl bg-terracotta text-white px-4 py-3 text-sm font-semibold transition-colors duration-200 hover:bg-terracotta-soft shadow-glow-teal ${className}`}>
      <Unlink className="w-[18px] h-[18px]" /> Break the Chain
    </button>
  );
}
